import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Crown, PencilSimple, Eye } from "@phosphor-icons/react";

const GRANT_ROLE_OPTIONS = [
  { role: "admin", label: "Admin", Icon: Crown },
  { role: "member", label: "Member", Icon: PencilSimple },
  { role: "viewer", label: "Viewer", Icon: Eye },
];

// The Grant existing user access dialog on Admin — extracted verbatim (no
// behavior change).
export function GrantAccessDialog({
  open, onOpenChange, currentTeamName, grantQuery, setGrantQuery, setGrantSelected,
  grantSearching, grantResults, grantSelected, grantRole, setGrantRole, grantError, grantSaving,
  onSubmit, inputCls,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white" data-testid="grant-access-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Grant existing user access</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Give someone who already has an account (e.g. an owner or member on another team) access to {currentTeamName} too — no new login needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="font-label text-xs text-zinc-400 block mb-2">Search by name or email</label>
            <input
              value={grantQuery}
              onChange={(e) => { setGrantQuery(e.target.value); setGrantSelected(null); }}
              data-testid="grant-search-input"
              placeholder="Type at least 2 characters…"
              className={inputCls}
            />
          </div>
          {grantSearching && <p className="text-zinc-500 text-xs">Searching…</p>}
          {!grantSelected && grantResults.length > 0 && (
            <div className="border border-zinc-800 rounded-sm max-h-40 overflow-y-auto" data-testid="grant-search-results">
              {grantResults.map((u) => (
                <button
                  type="button"
                  key={u.user_id}
                  onClick={() => setGrantSelected(u)}
                  data-testid={`grant-search-result-${u.user_id}`}
                  className="w-full text-left px-4 py-2.5 hover:bg-white/5 border-b border-zinc-900 last:border-b-0"
                >
                  <p className="text-white text-sm">{u.name}</p>
                  <p className="text-zinc-500 text-xs">{u.email}</p>
                </button>
              ))}
            </div>
          )}
          {!grantSelected && grantQuery.trim().length >= 2 && !grantSearching && grantResults.length === 0 && (
            <p className="text-zinc-600 text-xs" data-testid="grant-search-empty">No matching users found.</p>
          )}
          {grantSelected && (
            <div className="flex items-center justify-between bg-[#0A0A0A] border border-zinc-800 rounded-sm px-4 py-3" data-testid="grant-selected-user">
              <div>
                <p className="text-white text-sm font-semibold">{grantSelected.name}</p>
                <p className="text-zinc-500 text-xs">{grantSelected.email}</p>
              </div>
              <button type="button" onClick={() => { setGrantSelected(null); setGrantQuery(""); }} data-testid="grant-clear-selection" className="text-zinc-500 hover:text-white text-xs font-label">Change</button>
            </div>
          )}
          <div>
            <label className="font-label text-xs text-zinc-400 block mb-2">Role on this team</label>
            <div className="flex gap-2">
              {GRANT_ROLE_OPTIONS.map(({ role, label, Icon }) => (
                <button
                  key={role}
                  type="button"
                  onClick={() => setGrantRole(role)}
                  data-testid={`grant-role-${role}`}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm font-label text-xs transition-colors duration-150 ${grantRole === role ? "bg-[#CCFF00] text-black" : "bg-[#0A0A0A] border border-zinc-800 text-zinc-400 hover:text-white"}`}
                >
                  <Icon size={14} weight="bold" /> {label}
                </button>
              ))}
            </div>
          </div>
          {grantError && <p className="text-[#FF3B30] text-sm" data-testid="grant-access-error">{grantError}</p>}
        </div>
        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} className="font-label text-xs px-4 py-2 text-zinc-400 hover:text-white">Cancel</button>
          <button type="button" onClick={onSubmit} disabled={grantSaving} data-testid="confirm-grant-access-btn" className="bg-[#CCFF00] text-black font-label text-xs px-5 py-2 rounded-sm hover:bg-[#B3E600] transition-colors duration-150 disabled:opacity-50">
            {grantSaving ? "Granting…" : "Grant access"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
